/** Max variants in a clothing compare session (desktop columns). */
export const FAIR_VARIANT_COMPARE_MAX = 4

/** Min variants required to open full compare. */
export const FAIR_VARIANT_COMPARE_MIN = 2

/** Shops preview rows per column before “+N more”. */
export const FAIR_VARIANT_COMPARE_SHOPS_PREVIEW = 3

/** Speed-dial tray expansion: linear stack vs semi-circle fan. */
export type FairVariantCompareTrayLayout = 'linear' | 'semi-circle'

export const FAIR_VARIANT_COMPARE_TRAY_LAYOUTS: FairVariantCompareTrayLayout[] = [
  'linear',
  'semi-circle',
]

/** Vertical step between dial actions (linear). */
export const FAIR_VARIANT_COMPARE_TRAY_LINEAR_STEP_PX = 52

/** Arc radius for semi-circle dial. */
export const FAIR_VARIANT_COMPARE_TRAY_SEMI_RADIUS_PX = 96

export interface FairVariantCompareTrayDialOffset {
  x: number
  y: number
}

/**
 * Offset from FAB center for dial action at `index` (0 = closest to FAB).
 * Semi-circle opens left (FAB sits on the right edge of the viewport).
 */
export function getFairVariantCompareTrayDialOffset(
  index: number,
  count: number,
  layout: FairVariantCompareTrayLayout,
  options?: {
    itemStep?: number
    radius?: number
  },
): FairVariantCompareTrayDialOffset {
  if (count <= 0 || index < 0 || index >= count) {
    return { x: 0, y: 0 }
  }

  if (layout === 'linear') {
    const step = options?.itemStep ?? FAIR_VARIANT_COMPARE_TRAY_LINEAR_STEP_PX
    return { x: 0, y: -(index + 1) * step }
  }

  const radius = options?.radius ?? FAIR_VARIANT_COMPARE_TRAY_SEMI_RADIUS_PX
  if (count === 1) {
    return { x: -radius, y: 0 }
  }

  // t = -π/2 (up) … +π/2 (down); x = −cos(t)·r → leftward fan
  const start = -Math.PI / 2
  const end = Math.PI / 2
  const t = start + ((end - start) * index) / (count - 1)
  return {
    x: -Math.cos(t) * radius,
    y: Math.sin(t) * radius,
  }
}

export function nextFairVariantCompareTrayLayout(
  current: FairVariantCompareTrayLayout,
): FairVariantCompareTrayLayout {
  return current === 'linear' ? 'semi-circle' : 'linear'
}

export interface FairVariantCompareItem {
  productId: number
  variantId: number
  brand: string
  title: string
  price: string
  oldPrice?: string
  imageUrl?: string
  color?: string
  material?: string
}

export interface FairVariantCompareShop {
  id: string | number
  name: string
  price: string
  logoUrl?: string
  url?: string
}

export type FairVariantCompareDiffKey =
  | 'brand'
  | 'price'
  | 'discount'
  | 'color'
  | 'material'
  | 'shops'

export interface FairVariantCompareDiffRow {
  key: FairVariantCompareDiffKey
  labelKey: string
  values: Array<string | undefined>
  /** Indices that share the “best” value (e.g. lowest price). */
  highlightIndices: number[]
  /** True when at least two columns differ. */
  differs: boolean
}

export function parseFairVariantComparePriceAmount(
  price: string | undefined,
): number | null {
  if (!price) {
    return null
  }
  const match = price.replace(/\s/g, '').match(/-?\d+(?:[.,]\d+)?/)
  if (!match) {
    return null
  }
  const amount = Number(match[0].replace(',', '.'))
  return Number.isFinite(amount) ? amount : null
}

function discountLabel(item: FairVariantCompareItem): string | undefined {
  const current = parseFairVariantComparePriceAmount(item.price)
  const previous = parseFairVariantComparePriceAmount(item.oldPrice)
  if (current == null || previous == null || previous <= current) {
    return undefined
  }
  const percent = Math.round(((previous - current) / previous) * 100)
  if (percent <= 0) {
    return undefined
  }
  return `−${percent}%`
}

function valuesDiffer(values: Array<string | undefined>): boolean {
  const present = values.filter((value) => Boolean(value))
  if (present.length < 2) {
    return false
  }
  return new Set(present).size > 1
}

function lowestPriceIndices(items: FairVariantCompareItem[]): number[] {
  const amounts = items.map((item) =>
    parseFairVariantComparePriceAmount(item.price),
  )
  const finite = amounts.filter((amount): amount is number => amount != null)
  if (finite.length < 2) {
    return []
  }
  const min = Math.min(...finite)
  return amounts
    .map((amount, index) => (amount === min ? index : -1))
    .filter((index) => index >= 0)
}

function mostShopsIndices(counts: number[]): number[] {
  if (counts.length < 2 || counts.every((count) => count === 0)) {
    return []
  }
  const max = Math.max(...counts)
  if (max <= 0) {
    return []
  }
  return counts
    .map((count, index) => (count === max ? index : -1))
    .filter((index) => index >= 0)
}

/**
 * Build attribute rows for the compare showcase.
 * Rows without any values are omitted; identical rows stay but marked `differs: false`.
 */
export function buildFairVariantCompareDiffRows(
  items: FairVariantCompareItem[],
  shopCounts?: number[],
): FairVariantCompareDiffRow[] {
  if (items.length === 0) {
    return []
  }

  const brandValues = items.map((item) => item.brand || undefined)
  const priceValues = items.map((item) => item.price || undefined)
  const discountValues = items.map((item) => discountLabel(item))
  const colorValues = items.map((item) => item.color)
  const materialValues = items.map((item) => item.material)
  const counts =
    shopCounts && shopCounts.length === items.length
      ? shopCounts
      : items.map(() => 0)
  const shopValues = counts.map((count) =>
    count > 0 ? String(count) : undefined,
  )

  const candidates: FairVariantCompareDiffRow[] = [
    {
      key: 'brand',
      labelKey: 'fair_compare.diff.brand',
      values: brandValues,
      highlightIndices: [],
      differs: valuesDiffer(brandValues),
    },
    {
      key: 'price',
      labelKey: 'fair_compare.diff.price',
      values: priceValues,
      highlightIndices: lowestPriceIndices(items),
      differs: valuesDiffer(priceValues),
    },
    {
      key: 'discount',
      labelKey: 'fair_compare.diff.discount',
      values: discountValues,
      highlightIndices: [],
      differs: valuesDiffer(discountValues),
    },
    {
      key: 'color',
      labelKey: 'fair_compare.diff.color',
      values: colorValues,
      highlightIndices: [],
      differs: valuesDiffer(colorValues),
    },
    {
      key: 'material',
      labelKey: 'fair_compare.diff.material',
      values: materialValues,
      highlightIndices: [],
      differs: valuesDiffer(materialValues),
    },
    {
      key: 'shops',
      labelKey: 'fair_compare.diff.shops',
      values: shopValues,
      highlightIndices: mostShopsIndices(counts),
      differs: valuesDiffer(shopValues),
    },
  ]

  return candidates.filter((row) => row.values.some(Boolean))
}

/** Histoire / empty-page demo set */
export const FAIR_VARIANT_COMPARE_DEMO_ITEMS: FairVariantCompareItem[] = [
  {
    productId: 101,
    variantId: 1001,
    brand: 'The Row',
    title: 'Wool wrap coat',
    price: '890 BYN',
    oldPrice: '1120 BYN',
    imageUrl:
      'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?auto=format&fit=crop&w=600&h=800&q=80',
    color: 'Camel',
    material: 'Wool 80%, Cashmere 20%',
  },
  {
    productId: 102,
    variantId: 1002,
    brand: 'Totême',
    title: 'Double-face wool coat',
    price: '720 BYN',
    imageUrl:
      'https://images.unsplash.com/photo-1544022613-e87ca75a784a?auto=format&fit=crop&w=600&h=800&q=80',
    color: 'Black',
    material: 'Wool 100%',
  },
  {
    productId: 103,
    variantId: 1003,
    brand: 'COS',
    title: 'Belted tailored coat',
    price: '450 BYN',
    oldPrice: '590 BYN',
    imageUrl:
      'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?auto=format&fit=crop&w=600&h=800&q=80',
    color: 'Ivory',
    material: 'Wool 70%, Polyamide 30%',
  },
]

export const FAIR_VARIANT_COMPARE_DEMO_SHOPS: Record<
  number,
  FairVariantCompareShop[]
> = {
  1001: [
    { id: 'f1', name: 'Farfetch', price: '890 BYN', url: '#' },
    { id: 'f2', name: 'Matches', price: '920 BYN', url: '#' },
    { id: 'f3', name: 'SSENSE', price: '945 BYN', url: '#' },
  ],
  1002: [
    { id: 't1', name: 'Farfetch', price: '720 BYN', url: '#' },
    { id: 't2', name: 'Mytheresa', price: '745 BYN', url: '#' },
  ],
  1003: [
    { id: 'c1', name: 'COS', price: '450 BYN', url: '#' },
    { id: 'c2', name: 'Farfetch', price: '465 BYN', url: '#' },
    { id: 'c3', name: 'LS', price: '480 BYN', url: '#' },
    { id: 'c4', name: 'Zalando', price: '499 BYN', url: '#' },
  ],
}

/** Demo shops for Home demo cards (negative ids) and Histoire fallbacks. */
export function getFairVariantCompareDemoShops(
  variantId: number,
): FairVariantCompareShop[] {
  if (FAIR_VARIANT_COMPARE_DEMO_SHOPS[variantId]) {
    return FAIR_VARIANT_COMPARE_DEMO_SHOPS[variantId]
  }
  const pool = [
    { name: 'Farfetch', price: '39$' },
    { name: 'Wildberries', price: '42$' },
    { name: 'Lamoda', price: '45$' },
    { name: 'SSENSE', price: '48$' },
  ]
  const count = 2 + (Math.abs(variantId) % 3)
  return pool.slice(0, count).map((shop, index) => ({
    id: `${variantId}-${index}`,
    name: shop.name,
    price: shop.price,
  }))
}
